import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
// Aliased: the `api` prop below is the container client, this is cloud's.
import { api as cloud, type SessionUsage } from "../../lib/api"
import type { PromptFile } from "../../lib/attachments"
import { OpenCodeApi } from "../../lib/opencodeApi"
import type { FileSystemEntry, Message, ModelRef, Session } from "../../lib/opencodeTypes"
import type { CommandInfo } from "../../lib/opencodeApi"
import { sessionContext } from "../../lib/sessionContext"
import { readShowReasoning, saveShowReasoning } from "../../lib/sessionDisplay"
import {
  applySessionEvent,
  errorText,
  generatedSessionTitle,
  isPlaceholderSessionTitle,
  isStepEnded,
  isTurnActive,
  lastSwitchedAgent,
  lastSwitchedModel,
  sessionStatus,
  type SessionEvent,
  type SessionStatus,
} from "../../lib/sessionEvents"
import { BUILT_IN_COMMANDS, sameModel, type SlashCommand } from "../../lib/slashCommands"
import { useLoad, useToast } from "../ui"
import { ContextPanel } from "./ContextPanel"
import { MessageInput } from "./MessageInput"
import { MessageList } from "./MessageList"
import { PermissionDialog } from "./PermissionDialog"
import { QuestionDialog } from "./QuestionDialog"
import { RetryableError } from "./RetryableError"
import { StatusBar } from "./StatusBar"

// Kept in step with OUR_PROVIDER in src/workspace-sessions/proxy.ts, which is
// what enforces it.
const OUR_PROVIDER = "moderniser"

interface ChatProps {
  api: OpenCodeApi
  session: Session
  /** The cloud workspace-session id — the one in the URL, not the agent's. It
   *  is what the ledger attributes this container's inference to, so it is what
   *  the cost figure is looked up by. */
  workspaceSessionId: string
  contextContainer: HTMLElement | null
  contextSelected: boolean
  onShowContext: () => void
  onTitleChange?: (title: string) => void
}

export function Chat({
  api,
  session,
  workspaceSessionId,
  contextContainer,
  contextSelected,
  onShowContext,
  onTitleChange,
}: ChatProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [thinking, setThinking] = useState(false)
  // What the agent says it is doing, where it says so. isTurnActive below stays
  // as the fallback: it is derived from events that have always been handled, so
  // a server that does not publish session.status still gets a spinner.
  const [status, setStatus] = useState<SessionStatus | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  // What was just picked, until the transcript catches up. Not decoration:
  // switchModel publishes nothing when the ref is unchanged, so a pick that
  // happens to match a stale `session.model` would otherwise never confirm.
  const [pending, setPending] = useState<ModelRef | null>(null)
  // Same contract for the agent (Build / Plan).
  const [pendingAgent, setPendingAgent] = useState<string | null>(null)
  const [stopping, setStopping] = useState(false)
  const [title, setTitle] = useState(session.title)
  const [showReasoning, setShowReasoning] = useState(readShowReasoning)

  const reload = useCallback(() => setReloadKey((k) => k + 1), [])
  const toast = useToast()

  const updateShowReasoning = useCallback((show: boolean) => {
    setShowReasoning(show)
    saveShowReasoning(show)
  }, [])

  const acceptTitle = useCallback((next: string | undefined) => {
    if (isPlaceholderSessionTitle(next) || next === title) return
    setTitle(next!)
    onTitleChange?.(next!)
  }, [onTitleChange, title])

  useEffect(() => acceptTitle(session.title), [acceptTitle, session.title])

  // What this session has cost, from cloud's ledger.
  //
  // Not from the transcript, even though v1 does report a figure per message:
  // that is the agent's own cost basis, and the ledger is the only number with
  // this company's margin applied — the one the customer is actually charged.
  const [cost, setCost] = useState<string | undefined>()
  // The company that pays, from the same response. Known before the first
  // turn, so it shows even while the cost is still an em dash.
  const [billedTo, setBilledTo] = useState<string | undefined>()
  const costTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // The transcript as the event handler sees it. `consume` below is created
  // once per subscription and would otherwise close over the first render's
  // empty list.
  const messagesRef = useRef<Message[]>([])
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const loadCost = useCallback(async () => {
    try {
      const res = await cloud.get<SessionUsage>(`/workspace-sessions/${workspaceSessionId}/usage`)
      // No rows yet — a session created before this was wired, or one that has
      // not prompted. An em dash is the honest answer; "$0.00" is a claim.
      setCost(res.usage.records > 0 ? res.chargedFormatted : undefined)
      setBilledTo(res.billedTo?.name)
    } catch {
      // Never surfaced. A cost that cannot be read is a missing number in a
      // status bar, not a reason to put an error over someone's conversation.
      setCost(undefined)
      setBilledTo(undefined)
    }
  }, [workspaceSessionId])

  // Debounced, and deliberately trailing. The gateway reports usage to cloud
  // *after* it has returned the response (gateway/src/meter/report.ts keeps
  // billing off the client's latency path), so asking the instant a step ends
  // reads the ledger before the row lands. A turn also ends several steps, and
  // each one would otherwise be a request.
  const refreshCost = useCallback(() => {
    clearTimeout(costTimer.current)
    costTimer.current = setTimeout(() => void loadCost(), 1_500)
  }, [loadCost])

  useEffect(() => {
    void loadCost()
    return () => clearTimeout(costTimer.current)
  }, [loadCost])

  // Narrowed to our own provider, because the cloud proxy refuses a prompt
  // naming any other one (refuseForeignProvider in
  // src/workspace-sessions/proxy.ts) — offering a model that cannot be picked is
  // a broken menu row, not a choice. The container's config says the same with
  // `enabled_providers`; this is the belt to that brace.
  const models = useLoad(async () => {
    const list = await api.listModels()
    return list.filter((model) => model.providerID === OUR_PROVIDER)
  }, [api])
  const modelsError = models.error
    ? models.error instanceof Error
      ? models.error.message
      : String(models.error)
    : null

  // The agent modes the container declares — Build, Plan, and any custom ones.
  // Subagents and hidden agents are working parts, not modes a person picks;
  // the desktop applies the same filter.
  const agents = useLoad(async () => {
    const list = await api.listAgents()
    return list.filter((agent) => agent.mode !== "subagent" && !agent.hidden)
  }, [api])

  // Custom commands from the agent's config (opencode.json). Merged with
  // built-in commands at display time in MessageInput.
  const customCommands = useLoad(async () => {
    const list = await api.listCommands()
    // Filter out commands that shadow built-in ones — the agent's config can
    // override, but we keep the built-in description for consistency.
    const builtInNames = new Set(BUILT_IN_COMMANDS.map((c) => c.name))
    return list
      .filter((cmd: CommandInfo) => !builtInNames.has(cmd.name))
      .map((cmd: CommandInfo) => ({
        name: cmd.name,
        description: cmd.description ?? `Run the ${cmd.name} command`,
        isCustom: true,
      })) as SlashCommand[]
  }, [api])

  // All commands available in the composer: built-in + custom from config.
  const commands = useMemo<SlashCommand[]>(() => {
    return [...BUILT_IN_COMMANDS, ...(customCommands.data ?? [])]
  }, [customCommands.data])

  const switched = lastSwitchedModel(messages)
  // In order: what was just picked, what answered last, the session's own
  // default, and failing all of those the first model on offer — so that a
  // fresh session has something to compact with and to show in the bar.
  const firstModel = models.data?.[0]
  const currentModel =
    pending ??
    switched ??
    session.model ??
    (firstModel ? { providerID: firstModel.providerID, id: firstModel.id } : undefined)
  const switchedAgent = lastSwitchedAgent(messages)
  // "build" is what the runner uses when nothing was ever switched — the same
  // default the desktop falls back to.
  const currentAgent = pendingAgent ?? switchedAgent ?? session.agent ?? "build"

  // The transcript is authoritative once it agrees. Known limitation: a switch
  // made in another tab does not beat a local pick until one of them lands here.
  useEffect(() => {
    if (pending && sameModel(pending, switched)) setPending(null)
  }, [pending, switched])
  useEffect(() => {
    if (pendingAgent && pendingAgent === switchedAgent) setPendingAgent(null)
  }, [pendingAgent, switchedAgent])

  // One effect owns both the history fetch and the live subscription, because
  // the ordering between them matters.
  //
  // The subscription opens *first* and buffers, then history is fetched, then
  // the buffer is replayed on top. Fetching first would drop every event that
  // fired while the request was in flight — which is exactly the window a
  // prompt sent from another tab lands in. The reducer upserts by id, so an
  // event already reflected in the fetched history is a no-op.
  useEffect(() => {
    let cancelled = false
    let ready = false
    let buffered: SessionEvent[] = []

    const consume = (event: SessionEvent) => {
      acceptTitle(generatedSessionTitle(event, session.id))
      setMessages((prev) => applySessionEvent(prev, event))
      // A turn spans several model steps with tool runs *between* them, and a
      // step ending is not the turn ending. Treating it as one made the
      // Working indicator — and with it the Stop button — vanish exactly while
      // a long tool ran, which is when someone most wants to stop. So steps
      // only ever switch the indicator ON; only the session-level idle (from
      // session.status / session.idle) switches it off.
      const active = isTurnActive(event)
      if (active === true) setThinking(true)
      const next = sessionStatus(event)
      if (next) {
        setStatus(next)
        if (next.type === "idle") setThinking(false)
      }
      // A step that ends clears a retry that is still on screen — a retry that
      // succeeded should stop being reported the moment it succeeds — but a
      // plain busy survives until the turn itself settles.
      if (active === false) setStatus((prev) => (prev?.type === "retry" ? { type: "idle" } : prev))
      if (isStepEnded(event)) {
        refreshCost()
        // Reconciliation, not the primary signal: the session-level idle can
        // be missed entirely when the event stream drops for a moment, and a
        // Working indicator nothing will ever clear is worse than one extra
        // request per step.
        void api
          .isActive(session.id)
          .then((running) => {
            if (!cancelled && !running) setThinking(false)
          })
          .catch(() => {})
      }
      // A turn that failed. The prompt call itself answers 202 and reports
      // nothing — an unknown model, a rejected image, a provider that is down
      // all arrive here and only here. When a reply exists the reducer has
      // already put the error on it; when the turn died before one did, this
      // is the only place it can be shown, or the spinner runs forever.
      if (event.type === "session.error") {
        setThinking(false)
        setStatus({ type: "idle" })
        const last = messagesRef.current[messagesRef.current.length - 1]
        if (last?.type !== "assistant") setError(errorText(event.data?.["error"]))
      }
    }

    // Fetch history and fold any events that arrived while the request was in
    // flight on top of it. Runs on mount and again after every reconnect,
    // because the live bus does not replay what was missed while it was down.
    const sync = async () => {
      ready = false
      try {
        // Alongside history: whether a turn is running right now. The event
        // stream only reports transitions, so a page loaded mid-turn would
        // otherwise show neither Working nor Stop until the next step starts.
        const [history, running] = await Promise.all([
          api.getMessages(session.id),
          api.isActive(session.id).catch(() => undefined),
        ])
        if (cancelled) return

        let next = history
        let active: boolean | undefined = running
        for (const event of buffered) {
          next = applySessionEvent(next, event)
          const turn = isTurnActive(event)
          if (turn !== undefined) active = turn
        }
        buffered = []
        ready = true

        setMessages(next)
        if (active !== undefined) setThinking(active)
        setError(null)
      } catch (err) {
        if (cancelled) return
        // Stay out of buffering mode on failure, or every later event would be
        // queued and never applied.
        ready = true
        setError(err instanceof Error ? err.message : "Failed to load messages")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    // Subscribing before the first sync is what stops events firing during the
    // fetch from being lost.
    const unsubscribe = api.subscribeToSessionLive(
      session.id,
      (event: SessionEvent) => {
        if (cancelled) return
        if (!ready) {
          buffered.push(event)
          return
        }
        consume(event)
      },
      (attempt: number) => {
        // attempt 0 is the initial connection, which the sync() below covers.
        if (!cancelled && attempt > 0) sync()
      },
    )

    setLoading(true)
    sync()

    return () => {
      cancelled = true
      unsubscribe()
    }
    // refreshCost is stable for a given workspace session, but it is called from
    // `consume` above, so it belongs here rather than in a closure that outlives
    // it.
  }, [api, session.id, reloadKey, refreshCost, acceptTitle])

  // No reload afterwards: the user message and the reply both arrive as events.
  // The boolean is what tells the composer whether it may throw away what the
  // user typed and attached.
  const handleSend = async (text: string, files: PromptFile[]): Promise<boolean> => {
    try {
      setSending(true)
      setError(null)
      setThinking(true)
      await api.sendMessage(session.id, text, files, currentModel, currentAgent)
      return true
    } catch (err) {
      setThinking(false)
      setError(err instanceof Error ? err.message : "Failed to send message")
      return false
    } finally {
      setSending(false)
    }
  }

  const handleSwitchModel = async (ref: ModelRef) => {
    setPending(ref)
    setError(null)
    try {
      await api.switchModel(session.id, ref)
    } catch (err) {
      setPending(null)
      setError(err instanceof Error ? err.message : "Failed to switch model")
    }
  }

  const handleSwitchAgent = async (name: string) => {
    setPendingAgent(name)
    setError(null)
    try {
      await api.switchAgent(session.id, name)
    } catch (err) {
      setPendingAgent(null)
      setError(err instanceof Error ? err.message : "Failed to switch agent")
    }
  }

  // No optimistic status change: interrupt is answered by the event stream
  // (the step fails/ends and status goes idle), and claiming "stopped" before
  // the runner agrees would just flicker if the turn finishes first.
  const handleStop = async () => {
    setStopping(true)
    try {
      await api.interrupt(session.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to stop")
    } finally {
      setStopping(false)
    }
  }

  // Slash command dispatcher. Built-in session commands are handled here;
  // custom commands are forwarded to the agent via POST /session/:id/command.
  const handleCommand = useCallback(
    async (name: string, args: string) => {
      setError(null)
      try {
        switch (name) {
          // The two built-in primary agents, as shortcuts. Same handler as the
          // picker in the composer, so the two cannot drift.
          case "plan":
          case "build":
            await handleSwitchAgent(name)
            break
          case "compact":
            await api.compact(session.id, currentModel)
            toast("Conversation compacted")
            break
          case "undo": {
            // Find the last user message and revert to it.
            const lastUserIdx = [...messages].reverse().findIndex((m) => m.type === "user")
            if (lastUserIdx === -1) {
              toast("Nothing to undo", "error")
              return
            }
            const lastUser = messages[messages.length - 1 - lastUserIdx]
            if (!lastUser) {
              toast("Nothing to undo", "error")
              return
            }
            await api.revert(session.id, lastUser.id)
            toast("Last message reverted")
            break
          }
          case "redo":
            await api.unrevert(session.id)
            toast("Reverted messages restored")
            break
          case "share": {
            const shared = await api.share(session.id)
            const url = `${window.location.origin}/sessions/${shared.id}`
            await navigator.clipboard.writeText(url)
            toast("Share link copied to clipboard")
            break
          }
          case "unshare":
            await api.unshare(session.id)
            toast("Share link removed")
            break
          case "export": {
            // Generate a Markdown transcript and trigger a download.
            const lines = messages.map((m) => {
              if (m.type === "user") {
                return `## You\n\n${m.text}\n`
              }
              if (m.type === "assistant") {
                const text = m.content
                  .map((p) => (p.type === "text" ? p.text : ""))
                  .filter(Boolean)
                  .join("\n")
                return `## Assistant\n\n${text}\n`
              }
              if (m.type === "system" || m.type === "synthetic") {
                return `## ${m.type === "system" ? "System" : "Synthetic"}\n\n${m.text}\n`
              }
              if (m.type === "shell") {
                return `## Shell\n\n\`\`\`\n${m.command}\n${m.output}\n\`\`\`\n`
              }
              if (m.type === "compaction") {
                return `## Compaction\n\n${m.summary}\n`
              }
              return ""
            })
            const md = `# Session: ${title || "Untitled"}\n\n${lines.join("\n---\n\n")}`
            const blob = new Blob([md], { type: "text/markdown" })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `${title || "session"}-${new Date().toISOString().slice(0, 10)}.md`
            a.click()
            URL.revokeObjectURL(url)
            toast("Transcript exported")
            break
          }
          default:
            // Custom command — forward to the agent.
            await api.sendCommand(session.id, name, args)
            break
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : `Command /${name} failed`)
      }
    },
    [api, session.id, messages, toast, currentModel, title],
  )

  // File search for @ autocomplete.
  const findFiles = useCallback(
    async (query: string): Promise<FileSystemEntry[]> => {
      try {
        return await api.findFiles(query, "file", 20)
      } catch {
        return []
      }
    },
    [api],
  )

  // How full the conversation is. Derived from the transcript rather than from
  // `session.tokens`, which is cumulative across the session and fetched once —
  // see the notes on both in lib/sessionContext.ts and lib/opencodeTypes.ts.
  const context = useMemo(
    () => sessionContext(messages, models.data ?? [], session.model),
    [messages, models.data, session.model],
  )

  // Which of the two signals wins.
  //
  // `status` is the agent's own word and is the better one — only it can say
  // "retrying, attempt 2, rate limited". `thinking` is derived from events that
  // have always been handled, so it is what a server that does not publish
  // session.status leaves us with.
  //
  // The one place they are combined rather than ranked: a stale idle while the
  // transcript says a step is running. session.status publishes idle when a
  // turn settles, and the next turn's busy can arrive after our own
  // step.started has already set thinking.
  const barStatus: SessionStatus | undefined = useMemo(() => {
    if (status && status.type !== "idle") return status
    if (thinking) return { type: "busy" }
    return status
  }, [status, thinking])

  // The bar owns the model, effort and context readouts, so the header is back
  // to identifying the session. The old subtitle carried the model and effort
  // because there was nowhere else to put them.
  return (
    <div className="relative flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="mx-auto w-full max-w-3xl p-4">
            <RetryableError error={error} onRetry={reload} />
          </div>
        )}
        <MessageList
          messages={messages}
          loading={loading}
          thinking={thinking}
          showReasoning={showReasoning}
        />
      </div>

      {contextContainer && createPortal(
        <ContextPanel
          session={{ ...session, title }}
          context={context}
          messages={messages}
          cost={cost}
          billedTo={billedTo}
          showReasoning={showReasoning}
          onShowReasoningChange={updateShowReasoning}
          onExport={() => void handleCommand("export", "")}
        />,
        contextContainer,
      )}

      <StatusBar
        context={context}
        status={barStatus}
        cost={cost}
        billedTo={billedTo}
        contextSelected={contextSelected}
        onShowContext={onShowContext}
      />

      <MessageInput
        onSend={handleSend}
        onSwitchModel={handleSwitchModel}
        onCommand={handleCommand}
        models={models.data}
        modelsError={modelsError}
        currentModel={currentModel}
        agents={agents.data}
        currentAgent={currentAgent}
        onSwitchAgent={handleSwitchAgent}
        commands={commands}
        findFiles={findFiles}
        draftKey={session.id}
        disabled={sending}
        working={Boolean(barStatus && barStatus.type !== "idle")}
        onStop={handleStop}
        stopping={stopping}
      />

      <PermissionDialog api={api} sessionId={session.id} />
      <QuestionDialog api={api} sessionId={session.id} />
    </div>
  )
}
